import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { Profile } from '@/hooks/useProfile.ts'
import { cn } from '@/utils/cn.ts'

interface ProfileFormProps {
  profile?: Profile | null
  loading?: boolean
  walletAddress?: string | null
  onSubmit: (data: {
    email: string
    name?: string
    phone?: string
    notify_email?: boolean
    notify_sms?: boolean
    notify_push?: boolean
  }) => Promise<void>
  submitLabel?: string
}

const inputClass =
  'w-full rounded-md border border-line bg-surface-muted px-4 py-3 font-inherit text-[0.9375rem] text-fg outline-none transition-[border-color] duration-fast placeholder:text-fg-muted focus:border-accent'

const labelClass = 'text-sm font-medium text-fg-secondary'

const labelRequiredClass = "after:ml-0.5 after:text-error after:content-['*']"

export function ProfileForm({
  profile,
  loading,
  walletAddress,
  onSubmit,
  submitLabel,
}: ProfileFormProps) {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [notifyEmail, setNotifyEmail] = useState(true)
  const [notifySms, setNotifySms] = useState(true)
  const [notifyPush, setNotifyPush] = useState(true)
  const [emailError, setEmailError] = useState('')

  useEffect(() => {
    if (profile) {
      const id = setTimeout(() => {
        setEmail(profile.email ?? '')
        setName(profile.name ?? '')
        setPhone(profile.phone ?? '')
        setNotifyEmail(profile.notify_email !== false)
        setNotifySms(profile.notify_sms !== false)
        setNotifyPush(profile.notify_push !== false)
      }, 0)
      return () => clearTimeout(id)
    }
  }, [profile])

  const validate = (): boolean => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t('profile.emailRequired'))
      return false
    }
    setEmailError('')
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    await onSubmit({
      email,
      name: name || undefined,
      phone: phone || undefined,
      notify_email: notifyEmail,
      notify_sms: notifySms,
      notify_push: notifyPush,
    })
  }

  return (
    <form className="flex max-w-[480px] flex-col gap-6" onSubmit={handleSubmit}>
      <div className="flex flex-col gap-1">
        <label
          className={cn(labelClass, labelRequiredClass)}
          htmlFor="profile-email"
        >
          {t('profile.emailLabel')}
        </label>
        <input
          id="profile-email"
          type="email"
          className={inputClass}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('profile.emailPlaceholder')}
          required
        />
        {emailError && (
          <span className="text-error text-[0.8125rem]">{emailError}</span>
        )}
      </div>

      {walletAddress && (
        <div className="flex flex-col gap-1">
          <label className={labelClass} htmlFor="profile-wallet-address">
            {t('profile.walletAddressLabel', {
              defaultValue: 'Wallet address',
            })}
          </label>
          <input
            id="profile-wallet-address"
            type="text"
            className={cn(inputClass, 'font-mono')}
            value={walletAddress}
            readOnly
            aria-readonly="true"
            tabIndex={-1}
          />
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label className={labelClass} htmlFor="profile-name">
          {t('profile.nameLabel')}
        </label>
        <input
          id="profile-name"
          type="text"
          className={inputClass}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('profile.namePlaceholder')}
        />
      </div>

      <div className="flex flex-col gap-1">
        <label className={labelClass} htmlFor="profile-phone">
          {t('profile.phoneLabel')}
        </label>
        <input
          id="profile-phone"
          type="tel"
          className={inputClass}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t('profile.phonePlaceholder')}
        />
      </div>

      <fieldset className="border-line m-0 rounded-md border p-6">
        <legend className="text-fg-secondary px-2 text-sm font-medium">
          {t('profile.notificationPreferences')}
        </legend>
        <label className="text-fg flex cursor-pointer items-center gap-2 py-1 text-[0.9375rem] [&+&]:mt-1">
          <input
            type="checkbox"
            className="accent-accent h-[1.125rem] w-[1.125rem]"
            checked={notifyEmail}
            onChange={(e) => setNotifyEmail(e.target.checked)}
          />
          <span>{t('profile.notifyEmail')}</span>
        </label>
        <label className="text-fg flex cursor-pointer items-center gap-2 py-1 text-[0.9375rem] [&+&]:mt-1">
          <input
            type="checkbox"
            className="accent-accent h-[1.125rem] w-[1.125rem]"
            checked={notifySms}
            onChange={(e) => setNotifySms(e.target.checked)}
          />
          <span>{t('profile.notifySms')}</span>
        </label>
        <label className="text-fg flex cursor-pointer items-center gap-2 py-1 text-[0.9375rem] [&+&]:mt-1">
          <input
            type="checkbox"
            className="accent-accent h-[1.125rem] w-[1.125rem]"
            checked={notifyPush}
            onChange={(e) => setNotifyPush(e.target.checked)}
          />
          <span>{t('profile.notifyPush')}</span>
        </label>
      </fieldset>

      <button
        type="submit"
        className="bg-gradient-brand text-fg-inverse duration-fast self-start rounded-md border-none px-6 py-3 text-[0.9375rem] font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={loading}
      >
        {loading
          ? t('profile.saving')
          : (submitLabel ?? t('profile.saveProfile'))}
      </button>
    </form>
  )
}
